import { supabase } from "@/integrations/supabase/client";

/**
 * Client-side reporter for security signals (denied RLS reads/writes,
 * rejected realtime subscriptions, etc.). Best-effort: failures are
 * swallowed so reporting never breaks UX.
 */

export type SecurityEventType =
  | "rls_denied"
  | "realtime_denied"
  | "unauthorized_rpc"
  | "suspicious_input"
  | "other";

export type SecuritySeverity = "info" | "warn" | "error" | "critical";

export interface SecurityEventInput {
  event_type: SecurityEventType;
  severity?: SecuritySeverity;
  resource?: string;
  action?: string;
  pg_error_code?: string;
  message?: string;
  client_context?: Record<string, unknown>;
}

// Local de-dup: avoid spamming the function with the same denial within 60s.
const recent = new Map<string, number>();
const DEDUP_MS = 60_000;

function dedupKey(e: SecurityEventInput) {
  return [
    e.event_type,
    e.resource ?? "",
    e.action ?? "",
    e.pg_error_code ?? "",
  ].join("|");
}

export async function reportSecurityEvent(event: SecurityEventInput) {
  try {
    const key = dedupKey(event);
    const now = Date.now();
    const last = recent.get(key);
    if (last && now - last < DEDUP_MS) return;
    recent.set(key, now);

    // Tag with current location for context.
    const client_context = {
      path: typeof window !== "undefined" ? window.location.pathname : "",
      ...(event.client_context ?? {}),
    };

    await supabase.functions.invoke("log-security-event", {
      body: { severity: "warn", ...event, client_context },
    });
  } catch {
    /* best effort */
  }
}

/**
 * Inspect a Supabase error and, if it looks like an RLS denial, report it.
 * Returns true when a report was queued.
 *
 * Postgres signals RLS denial with code "42501" or messages containing
 * "row-level security". A blocked SELECT often returns no rows + no error,
 * so callers should report those separately when context warrants it.
 */
export function reportIfRlsDenied(
  error: unknown,
  context: { resource: string; action: string; extra?: Record<string, unknown> }
): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { code?: string; message?: string };
  const msg = (e.message ?? "").toLowerCase();
  const isRls =
    e.code === "42501" ||
    msg.includes("row-level security") ||
    msg.includes("violates row-level security policy");
  if (!isRls) return false;
  reportSecurityEvent({
    event_type: "rls_denied",
    severity: "error",
    resource: context.resource,
    action: context.action,
    pg_error_code: e.code ?? "",
    message: (e.message ?? "").slice(0, 500),
    client_context: context.extra ?? {},
  });
  return true;
}
